import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  doubleOctave,
  flattenMidiNotes,
  getMidiBeatsPerBar,
  transposeMidiJson,
  trimLeadInBars,
} from '../utils/midiJson';
import {
  createMaster,
  createVoice,
  curveVelocity,
  getRingTime,
  loadTone,
  MIN_NOTE_DURATION,
  RELEASE_TAIL,
  resumeContext,
  scheduleMetronomeClick,
} from '../utils/toneVoices';
import { getInstrumentName } from '../utils/midiInstruments';
import i18n from '../i18n';

// What Tone's transport sits at out of the box, and what a clip without a tempo gets
const DEFAULT_BPM = 120;

// Shared so an idle player hands out the same object on every render rather than a fresh Set
const NO_ACTIVE_NOTES = new Set();

const haveSameNotes = (a, b) => {
  if (a.size !== b.size) return false;

  for (const midi of a) {
    if (!b.has(midi)) return false;
  }

  return true;
};

/**
 * The notes sounding at a point in the clip.
 *
 * Read off the transport clock each frame rather than pushed from the note callbacks: one pass
 * per frame means the keyboard re-renders at most 60 times a second no matter how dense the MIDI
 * is, and reading the same clock the synth plays from keeps the highlights in step with the
 * audio instead of drifting against it. `notes` is sorted by time, so the scan can stop at the
 * first note that has not started yet.
 *
 * Deliberately the note's own duration and not its ring time: a key lifts on screen when the
 * player's finger lifted, even though the pedal leaves it sounding. Highlighting the sustain
 * instead would hold a pedalled chord down for bars at a time and, worse, make a repeated note
 * look like one unbroken press rather than showing each restrike.
 */
const getSoundingNotes = (notes, seconds) => {
  const sounding = new Set();

  for (const note of notes) {
    if (note.time > seconds) break;
    if (note.time + Math.max(note.duration, MIN_NOTE_DURATION) > seconds) sounding.add(note.midi);
  }

  return sounding;
};

/**
 * Plays a part's MIDI JSON through a Tone synth.
 *
 * Notes are scheduled as a single Tone.Part on the transport, which gives sample-accurate
 * timing and — more importantly — a clean way to cancel everything still queued when the
 * user stops or closes the modal.
 *
 * @param {object} midiJson - MIDI JSON in the shape stored on a part
 * @param {string} instrumentId - Preset to play with; falls back to the default when unknown
 * @param {boolean} isActive - Whether playback is reachable, i.e. the modal is open. Tone is
 *   fetched at that point so the first play doesn't sit waiting on the download. Defaults to
 *   off: both modals stay mounted while closed, and eagerly pulling ~200KB into every song page
 *   is worse than the wait it saves.
 * @param {number} semitones - How far to shift the clip so it sounds in the key on screen.
 *   Applied here on the way to the synth rather than written back to the stored clip.
 * @param {number} bpmOverride - Tempo to actually play back at, for slowing a clip down to
 *   practice it. Falsy values play at the clip's own tempo. Pitch is untouched either way —
 *   these are synthesized notes, not recorded audio, so there is no time-stretch to speed up.
 * @param {object} options
 * @param {boolean} options.metronome - Click on every beat under the clip
 * @param {boolean} options.countIn - One bar of clicks before the first note
 * @param {number} options.metronomeVolume - How loud the click is, 0..1; null follows the metronome setting
 * @param {number} options.instrumentVolume - Level for the clip itself, 0..1 of the preset's own
 * @returns {{ isLoading: boolean, isPlaying: boolean, error: string, play: function,
 *   stop: function, duration: number, activeNotes: Set<number>, playbackMidi: object }}
 */
const useMidiPlayer = (
  midiJson,
  instrumentId,
  isActive = false,
  semitones = 0,
  bpmOverride = 0,
  { metronome = false, countIn = false, metronomeVolume = null, instrumentVolume = 1 } = {},
) => {
  const toneRef = useRef(null);
  const synthRef = useRef(null);
  const effectsRef = useRef(null);
  const gainRef = useRef(null);
  const partsRef = useRef([]);
  const masterRef = useRef(null);
  const endTimeoutRef = useRef(null);
  const frameRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState('');
  const [activeNotes, setActiveNotes] = useState(NO_ACTIVE_NOTES);

  // Read as each click is scheduled and ramped live, so a slider moves what is playing rather
  // than restarting it
  const volumeRef = useRef(metronomeVolume);
  volumeRef.current = metronomeVolume;

  const instrumentVolumeRef = useRef(instrumentVolume);
  instrumentVolumeRef.current = instrumentVolume;

  // What actually gets played and drawn. transposeMidiJson hands back the original object when
  // there is nothing to shift, so an untransposed clip keeps a stable identity and doesn't
  // retrigger the teardown effect below on every render.
  // Bars recorded empty ahead of the first note are dropped, so the count-in is the only lead-in,
  // and a clip set to double in octaves gets its mirrored notes here.
  const beatsPerBar = getMidiBeatsPerBar(midiJson);
  const playbackMidi = useMemo(
    () => trimLeadInBars(doubleOctave(transposeMidiJson(midiJson, semitones)), beatsPerBar),
    [midiJson, semitones, beatsPerBar],
  );

  const duration = playbackMidi?.duration || 0;

  // sourceBpm is what the clip's note times are written against — the transport sits at this
  // tempo while the Part is built, so ticks land where the file intends. playbackBpm is what it
  // actually plays back at; the two diverging is what lets a practice session slow a clip down
  // without touching the transposed pitch above.
  const sourceBpm = playbackMidi?.bpm || DEFAULT_BPM;
  const playbackBpm = bpmOverride || sourceBpm;

  // Tear down every piece of audio state. Cancelling the transport matters as much as disposing
  // the synth — notes scheduled but not yet fired would otherwise sound on the next play.
  const teardown = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    setActiveNotes(NO_ACTIVE_NOTES);

    if (endTimeoutRef.current) {
      clearTimeout(endTimeoutRef.current);
      endTimeoutRef.current = null;
    }

    partsRef.current.forEach((part) => {
      part.stop();
      part.dispose();
    });
    partsRef.current = [];

    if (toneRef.current) {
      const transport = toneRef.current.getTransport();

      transport.stop();
      transport.cancel();
    }

    if (synthRef.current) {
      synthRef.current.releaseAll();
      synthRef.current.dispose();
      synthRef.current = null;
    }

    // Between the voice and the master chain, matching where they sit in the signal path. Each
    // stage has to outlive the one feeding it, or the last notes are left routed through a node
    // that no longer exists.
    if (effectsRef.current) {
      effectsRef.current.forEach((node) => node.dispose());
      effectsRef.current = null;
    }

    if (gainRef.current) {
      gainRef.current.dispose();
      gainRef.current = null;
    }

    // After the voice, not before: disposing the master out from under a voice still connected to
    // it leaves the last notes routed to a dead node
    if (masterRef.current) {
      masterRef.current.dispose();
      masterRef.current = null;
    }
  }, []);

  // Fetch Tone while the user is looking at the modal rather than when they hit play
  useEffect(() => {
    if (!isActive || toneRef.current) return;

    let cancelled = false;

    loadTone().then(
      (Tone) => {
        if (!cancelled) toneRef.current = Tone;
      },
      () => {},
    );

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  useEffect(() => {
    return () => teardown();
  }, [teardown]);

  // Swapping the clip, the instrument, the key or the practice tempo mid-playback shouldn't
  // leave the previous one sounding — playbackMidi covers the clip and the transposition together
  useEffect(() => {
    teardown();
    setIsPlaying(false);
  }, [playbackMidi, instrumentId, playbackBpm, metronome, countIn, teardown]);

  // Ramped rather than set: a level jump on a sounding note is a click of its own
  useEffect(() => {
    if (gainRef.current) gainRef.current.gain.rampTo(instrumentVolume, 0.05);
  }, [instrumentVolume]);

  const stop = useCallback(() => {
    teardown();
    setIsPlaying(false);
  }, [teardown]);

  const play = useCallback(async () => {
    const notes = flattenMidiNotes(playbackMidi);

    if (notes.length === 0) return;

    setIsLoading(true);
    setError('');

    try {
      const Tone = toneRef.current || (await loadTone());
      toneRef.current = Tone;

      if (!(await resumeContext(Tone))) {
        setError(i18n.t('player.audioBlocked'));

        return;
      }

      teardown();

      const master = createMaster(Tone);
      masterRef.current = master;

      // Its own gain before the limiter, so the clip can be balanced against the click
      const gain = new Tone.Gain(instrumentVolumeRef.current).connect(master);
      gainRef.current = gain;

      // Sampled instruments download here if they haven't already. sampleLibrary memoises the
      // request, so this is instant on every play after the first — and a play that lands while
      // the picker is still downloading joins that fetch rather than starting another.
      const { voice: synth, preset, effects, fellBackFrom } = await createVoice(Tone, instrumentId, gain);
      synthRef.current = synth;
      effectsRef.current = effects;

      if (fellBackFrom) setError(i18n.t('player.fellBack', { from: fellBackFrom, to: getInstrumentName(preset) }));

      const transport = Tone.getTransport();

      // Note times are absolute seconds, but Part.add converts them to transport ticks as each
      // event is added — so the tempo has to be settled before the Part is built, or every note
      // lands at the wrong tick. The transport is global and holds whatever the last clip left
      // on it, hence setting it every time rather than once.
      transport.bpm.value = sourceBpm;

      const part = new Tone.Part(
        (time, note) => {
          synth.triggerAttackRelease(
            Tone.Frequency(note.midi, 'midi').toFrequency(),
            getRingTime(note),
            time,
            curveVelocity(note.velocity),
          );
        },
        notes.map((note) => [note.time, note]),
      );

      const secondsPerBeat = 60 / sourceBpm;
      const secondsPerBar = secondsPerBeat * beatsPerBar;
      const offset = countIn ? secondsPerBar : 0;

      part.start(offset);
      partsRef.current.push(part);

      // The count-in is heard even with the click off: a silent bar before the notes is just a wait
      const clickBars = (countIn ? 1 : 0) + (metronome ? Math.max(1, Math.ceil(duration / secondsPerBar - 0.01)) : 0);

      if (clickBars > 0) {
        const clickAt = (time, isDownbeat) => scheduleMetronomeClick(Tone, time, isDownbeat, volumeRef.current);
        const beats = Array.from({ length: clickBars * beatsPerBar }, (_, i) => [
          i * secondsPerBeat,
          i % beatsPerBar === 0,
        ]);
        const clickPart = new Tone.Part(clickAt, beats);

        clickPart.start(0);
        partsRef.current.push(clickPart);
      }

      // The Part's ticks are already fixed to sourceBpm's mapping, so switching the transport to
      // playbackBpm now changes only the rate they're played back at — every note's timing scales
      // uniformly, which is what a practice tempo needs.
      transport.bpm.value = playbackBpm;

      transport.position = 0;
      transport.start();

      // How much slower or faster the clip is actually running than the note times it was built
      // from, so the frame loop and end timer — both of which think in the clip's own seconds —
      // can convert real elapsed time back to that space.
      const tempoScale = playbackBpm / sourceBpm;

      const trackSoundingNotes = () => {
        const position = transport.seconds * tempoScale - offset;
        const sounding = position < 0 ? NO_ACTIVE_NOTES : getSoundingNotes(notes, position);

        setActiveNotes((previous) => (haveSameNotes(previous, sounding) ? previous : sounding));

        frameRef.current = requestAnimationFrame(trackSoundingNotes);
      };

      trackSoundingNotes();

      setIsPlaying(true);

      // A Tone.Part has no "finished" event — the transport just keeps rolling past the last
      // note — so the end of the clip is timed from its duration, scaled to real time the same
      // way the frame loop is. Presets that ring on after the last note-off — pads, choirs,
      // sustained strings — say so, or the teardown would chop their tail off.
      endTimeoutRef.current = setTimeout(
        () => {
          teardown();
          setIsPlaying(false);
        },
        ((offset + duration) / tempoScale + (preset.releaseTail || RELEASE_TAIL)) * 1000,
      );
    } catch (err) {
      // Audio failures are otherwise indistinguishable from silence, which makes them very
      // hard to track down — say so rather than swallowing it
      console.error('MIDI playback failed', err);
      setError(i18n.t('player.midiFailed'));
      teardown();
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  }, [playbackMidi, instrumentId, duration, sourceBpm, playbackBpm, beatsPerBar, metronome, countIn, teardown]);

  return { isLoading, isPlaying, error, play, stop, duration, activeNotes, playbackMidi };
};

export default useMidiPlayer;
